import { c as _c } from "react/compiler-runtime";
import { urlForImage } from "@/sanity/lib/image";
import type { ImageType, VideoType } from "@/sanity/query/project";
import Image from "next/image";
import React from "react";
import useDevice from "./useDevice";
import { Vimeo } from "./Vimeo";
type CarouselAssetProps = {
  asset: VideoType | ImageType;
  containerW: number;
  containerH: number;
  maxWRatio: number;
  maxHRatio: number;
};
export const CarouselAsset = React.forwardRef<HTMLDivElement, CarouselAssetProps>((t0, ref) => {
  const $ = _c(19);
  let asset;
  let containerH;
  let containerW;
  let maxHRatio;
  let maxWRatio;
  let props;
  if ($[0] !== t0) {
    ({
      asset,
      containerW,
      containerH,
      maxWRatio,
      maxHRatio,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asset;
    $[2] = containerH;
    $[3] = containerW;
    $[4] = maxHRatio;
    $[5] = maxWRatio;
    $[6] = props;
  } else {
    asset = $[1];
    containerH = $[2];
    containerW = $[3];
    maxHRatio = $[4];
    maxWRatio = $[5];
    props = $[6];
  }
  const {
    dpr
  } = useDevice();
  const isVimeo = "vimeo" in asset;
  let t1;
  if ($[7] !== asset.metadata || $[8] !== asset.url || $[9] !== asset.vimeo || $[10] !== containerH || $[11] !== containerW || $[12] !== dpr || $[13] !== isVimeo || $[14] !== maxHRatio || $[15] !== maxWRatio || $[16] !== props || $[17] !== ref) {
    const {
      width,
      height
    } = getImageDimensions({
      aspectRatio: isVimeo ? 1.7777777777777777 : asset.metadata.dimensions.aspectRatio,
      containerWidth: containerW,
      containerHeight: containerH,
      maxWidthRatio: maxWRatio,
      maxHeightRatio: maxHRatio
    });
    t1 = <div ref={ref} style={{
      height,
      width,
      position: "relative"
    }} {...props}>{isVimeo ? <Vimeo className="w-full" value={asset.vimeo} title="Project Video" /> : <Image src={urlForImage(asset.url).width(width).height(height).dpr(dpr).url()} alt="Project Image" fill={true} unoptimized={true} blurDataURL={asset.metadata.lqip} placeholder="blur" />}</div>;
    $[7] = asset.metadata;
    $[8] = asset.url;
    $[9] = asset.vimeo;
    $[10] = containerH;
    $[11] = containerW;
    $[12] = dpr;
    $[13] = isVimeo;
    $[14] = maxHRatio;
    $[15] = maxWRatio;
    $[16] = props;
    $[17] = ref;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  return t1;
});

// Run a object-contain algorithm to determine the best width and height for the image given the screen dimensions and max ratios.
export function getImageDimensions({
  aspectRatio,
  containerWidth,
  containerHeight,
  maxWidthRatio,
  maxHeightRatio
}: {
  aspectRatio: number;
  containerWidth: number;
  containerHeight: number;
  maxWidthRatio: number;
  maxHeightRatio: number;
}) {
  const maxWidth = maxWidthRatio * containerWidth;
  const maxHeight = maxHeightRatio * containerHeight;

  // Start by fitting to the maximum width.
  let width = maxWidth;
  let height = width / aspectRatio;

  // If that exceeds the maximum height, fit to height instead.
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  return {
    width: Math.round(width),
    height: Math.round(height)
  };
}